const defaultBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
const rawBase = process.env.REACT_APP_API_BASE || defaultBase;
const API_BASE = rawBase.endsWith('/') ? rawBase.slice(0, -1) : rawBase;
const AUTH_SESSION_KEY = 'auth_session_active';
const DEFAULT_REQUEST_TIMEOUT_MS = 15000;
let refreshPromise = null;

export class ApiError extends Error {
  constructor(status, detail, payload = null) {
    super(detail || 'API Fehler');
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

export function getStoredAuth() {
  return {
    sessionActive: localStorage.getItem(AUTH_SESSION_KEY) === '1',
  };
}

export function setStoredAuth() {
  localStorage.setItem(AUTH_SESSION_KEY, '1');
}

export function clearStoredAuth() {
  localStorage.removeItem(AUTH_SESSION_KEY);
  localStorage.removeItem('token');
}

async function parseResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      const text = await res.text().catch(() => '');
      return text ? { raw: text, parse_error: true } : { parse_error: true };
    }
  }
  const text = await res.text().catch(() => '');
  return text || null;
}

function extractDetail(payload, fallback = 'API Fehler') {
  if (!payload) return fallback;
  if (typeof payload === 'string') return payload;
  if (typeof payload.detail === 'string') return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail.length > 0) {
    const firstItem = payload.detail[0];
    if (typeof firstItem === 'string' && firstItem.trim()) return firstItem.trim();
    if (firstItem && typeof firstItem.msg === 'string' && firstItem.msg.trim()) {
      const fieldMap = {
        subject: 'Betreff',
        message: 'Nachricht',
        category: 'Kategorie',
        email: 'E-Mail-Adresse',
        password: 'Passwort',
        new_password: 'Neues Passwort',
        current_password: 'Aktuelles Passwort',
        code: 'Code',
        to_email: 'Empfänger-E-Mail',
        smtp_host: 'SMTP-Host',
        smtp_port: 'SMTP-Port',
        smtp_user: 'SMTP-Benutzer',
        smtp_from: 'Absender-Adresse',
        smtp_pass: 'SMTP-Passwort',
        billing_provider: 'Billing-Provider',
        billing_public_key: 'Billing Public Key',
        billing_secret_key: 'Billing Secret Key',
        billing_webhook_secret: 'Billing Webhook Secret',
        billing_success_url: 'Billing Erfolgs-URL',
        billing_cancel_url: 'Billing Abbruch-URL',
        ticker: 'Ticker',
        market: 'Markt',
        budget_per_title: 'Budget pro Titel',
        content: 'Inhalt',
      };
      const fieldKey = Array.isArray(firstItem.loc) ? firstItem.loc[firstItem.loc.length - 1] : null;
      const fieldLabel = typeof fieldKey === 'string' ? (fieldMap[fieldKey] || fieldKey) : null;
      let message = firstItem.msg.trim();

      if (/String should have at least (\d+) characters/i.test(message)) {
        const min = message.match(/String should have at least (\d+) characters/i)?.[1];
        message = fieldLabel
          ? `${fieldLabel} muss mindestens ${min} Zeichen lang sein`
          : `Eingabe muss mindestens ${min} Zeichen lang sein`;
      } else if (/String should have at most (\d+) characters/i.test(message)) {
        const max = message.match(/String should have at most (\d+) characters/i)?.[1];
        message = fieldLabel
          ? `${fieldLabel} darf höchstens ${max} Zeichen lang sein`
          : `Eingabe darf höchstens ${max} Zeichen lang sein`;
      } else if (/Input should be greater than 0/i.test(message)) {
        message = fieldLabel
          ? `${fieldLabel} muss größer als 0 sein`
          : 'Wert muss größer als 0 sein';
      } else if (/Input should be greater than or equal to (\d+)/i.test(message)) {
        const min = message.match(/Input should be greater than or equal to (\d+)/i)?.[1];
        message = fieldLabel
          ? `${fieldLabel} muss mindestens ${min} sein`
          : `Wert muss mindestens ${min} sein`;
      } else if (/Input should be less than or equal to (\d+)/i.test(message)) {
        const max = message.match(/Input should be less than or equal to (\d+)/i)?.[1];
        message = fieldLabel
          ? `${fieldLabel} darf höchstens ${max} sein`
          : `Wert darf höchstens ${max} sein`;
      } else if (/Field required/i.test(message)) {
        message = fieldLabel ? `${fieldLabel} ist erforderlich` : 'Pflichtfeld fehlt';
      } else if (fieldLabel && !message.startsWith(fieldLabel)) {
        message = `${fieldLabel}: ${message}`;
      }

      return message;
    }
  }
  if (typeof payload.raw === 'string' && payload.raw.trim()) return payload.raw.trim();
  if (payload.parse_error) return `${fallback} (Antwort konnte nicht als JSON gelesen werden)`;
  return fallback;
}

async function refreshTokens() {
  const res = await fetch(`${API_BASE}/token/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
    credentials: 'include',
  });

  const payload = await parseResponse(res);
  if (!res.ok) {
    throw new ApiError(res.status, extractDetail(payload, 'Session konnte nicht verlängert werden'), payload);
  }
}

async function ensureFreshSession() {
  if (!refreshPromise) {
    refreshPromise = refreshTokens()
      .then(() => {
        setStoredAuth();
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

async function authorizedFetch(path, options = {}) {
  const {
    method = 'GET',
    body,
    headers = {},
    auth = true,
    retryOnUnauthorized = true,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  } = options;

  const authState = getStoredAuth();
  const finalHeaders = { ...headers };

  let finalBody = body;
  if (body !== undefined && body !== null && !(body instanceof FormData) && typeof body !== 'string') {
    finalHeaders['Content-Type'] = finalHeaders['Content-Type'] || 'application/json';
    finalBody = JSON.stringify(body);
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller
    ? window.setTimeout(() => controller.abort(), timeoutMs)
    : null;

  let res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: finalHeaders,
    body: finalBody,
    credentials: 'include',
    signal: controller?.signal,
  });

  if (res.status === 401 && auth && retryOnUnauthorized && authState.sessionActive) {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
    await ensureFreshSession();
    const retryController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const retryTimeoutId = retryController
      ? window.setTimeout(() => retryController.abort(), timeoutMs)
      : null;
    const retryHeaders = { ...finalHeaders };
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: retryHeaders,
      body: finalBody,
      credentials: 'include',
      signal: retryController?.signal,
    });
    if (retryTimeoutId !== null) {
      window.clearTimeout(retryTimeoutId);
    }
    return res;
  }

  if (timeoutId !== null) {
    window.clearTimeout(timeoutId);
  }

  return res;
}

export async function apiRequest(path, options = {}) {
  const {
    method = 'GET',
    body,
    headers = {},
    auth = true,
    retryOnUnauthorized = true,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  } = options;

  let res;
  try {
    res = await authorizedFetch(path, { method, body, headers, auth, retryOnUnauthorized, timeoutMs });
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }
    if (err?.name === 'AbortError') {
      throw new ApiError(408, 'Request-Timeout. Bitte erneut versuchen.');
    }
    throw new ApiError(0, 'Netzwerkfehler. Bitte erneut versuchen.');
  }

  const payload = await parseResponse(res);
  if (!res.ok) {
    throw new ApiError(res.status, extractDetail(payload), payload);
  }

  return payload;
}

export async function apiBlobRequest(path, options = {}) {
  let res;
  try {
    res = await authorizedFetch(path, options);
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }
    if (err?.name === 'AbortError') {
      throw new ApiError(408, 'Request-Timeout. Bitte erneut versuchen.');
    }
    throw new ApiError(0, 'Netzwerkfehler. Bitte erneut versuchen.');
  }

  if (!res.ok) {
    const payload = await parseResponse(res);
    throw new ApiError(res.status, extractDetail(payload), payload);
  }

  return res.blob();
}
